import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';

import * as Sentry from '@sentry/remix';
import { CardElement } from '@stripe/react-stripe-js';
import type { Stripe } from '@stripe/stripe-js';
import { t } from 'i18next';

import { ACTIVE_UPGRADE_COOKIE_NAME } from '~/hooks/useUtils/useCookies';

interface Props {
  stripe: Stripe | null;
  elements: any;
  isSuccessful: boolean;
  isFailed: boolean;
  data: any;
  hasRedirected: boolean;
  applePayEvent: any;
  setApplePayEvent: (event: any) => void;
  setCookie: (options: any) => void;
  removeCookie: (name: string) => void;
  setHasRedirected: (hasRedirected: boolean) => void;
  onContinue?: () => void;
  handleRemoveDisabledState: () => void;
  isGift?: boolean;
}

const useHandleCheckoutSubmissionStates = ({
  stripe,
  elements,
  isSuccessful,
  isFailed,
  data,
  hasRedirected,
  applePayEvent,
  setApplePayEvent,
  setCookie,
  removeCookie,
  setHasRedirected,
  onContinue,
  handleRemoveDisabledState,
  isGift = false,
}: Props) => {
  const [isProcessing3DSecure, setIsProcessing3DSecure] = useState(false);

  const removeApplePayEvent = (status: 'success' | 'fail') => {
    if (applePayEvent) {
      applePayEvent.complete(status);
      setApplePayEvent(null);
    }
  };

  const handleSuccess = () => {
    removeApplePayEvent('success');
    if (isGift) {
      toast.success(t('upgrade.tabs.gift.success.message'));
    } else {
      toast.success(t('upgrade.success.message'));
    }
    onContinue?.();
    setHasRedirected(true);
    if (!isGift) {
      setCookie({ name: ACTIVE_UPGRADE_COOKIE_NAME, value: 'true', days: 4 });
    }
    setIsProcessing3DSecure(false);
    handleRemoveDisabledState();
  };

  const handleFail = (errorMessage?: string) => {
    removeApplePayEvent('fail');
    toast.error(errorMessage || data?.toast?.message || t('errors.default'));
    if (!isGift) {
      removeCookie(ACTIVE_UPGRADE_COOKIE_NAME);
    }
    setIsProcessing3DSecure(false);
    handleRemoveDisabledState();
  };

  const clientSecret = data?.clientSecret;
  const action = data?.action;

  const confirmIntent = (clientSecret: string) => {
    if (action === 'requires_confirmation') {
      return stripe?.confirmCardSetup(clientSecret, {
        payment_method: { card: elements.getElement(CardElement) },
      });
    }

    if (action === 'requires_action') {
      return stripe?.confirmCardPayment(clientSecret, {
        payment_method: { card: elements.getElement(CardElement) },
      });
    }

    if (applePayEvent) {
      return stripe?.confirmCardPayment(clientSecret, {
        payment_method: applePayEvent.paymentMethod.id,
      });
    }

    return undefined;
  };

  const handleConfirmPayment = async (clientSecret: string) => {
    setIsProcessing3DSecure(true);

    try {
      const result = await confirmIntent(clientSecret);

      // Stripe resolves these with an `error` payload rather than throwing, so a decline or an
      // abandoned 3DS challenge lands here looking identical to a completed payment.
      const confirmed =
        (result && 'setupIntent' in result && result.setupIntent?.status === 'succeeded') ||
        (result && 'paymentIntent' in result && result.paymentIntent?.status === 'succeeded');

      if (confirmed) {
        handleSuccess();
      } else {
        handleFail(result?.error?.message);
      }
    } catch (error) {
      Sentry.captureException(error);
      handleFail();
    }
  };

  // On Attempts that where additional authentication or actions by the user are required
  useEffect(() => {
    if (clientSecret && action) {
      handleConfirmPayment(clientSecret);
    }
  }, [clientSecret, action]);

  // On successful or failed attemps
  useEffect(() => {
    if (isSuccessful && !hasRedirected) {
      handleSuccess();
    } else if (isFailed) {
      handleFail();
    }
  }, [isSuccessful, isFailed, hasRedirected]);

  return { isProcessing3DSecure };
};

export default useHandleCheckoutSubmissionStates;
